import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format, parseISO, parse, isValid } from 'date-fns'; // Import parseISO, parse, and isValid
import { PlusCircle, AlertCircle } from 'lucide-react'; // Added icons
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'; // Added Alert components
import { Skeleton } from '@/components/ui/skeleton'; // Added Skeleton
import { fetchApi } from '@/lib/api'; // <-- Import fetchApi

// Define the expected shape of an event from the API
interface ApiEvent {
  id: number;
  user_id: number;
  name: string;
  event_date: string | null; // Matches sql.NullTime JSON marshal
  event_link_id: string;
  created_at: string;
}

// NOTE: Route path is now relative to the parent layout route (_authenticated)
export const Route = createFileRoute('/_authenticated/events/')({
  component: EventsListPage,
});

function EventsListPage() {
  const [events, setEvents] = useState<ApiEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchApi('/api/admin/events'); // <-- Use fetchApi
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: ApiEvent[] = await response.json();
        setEvents(data);
      } catch (err) {
        console.error('Failed to fetch events:', err);
        setError(
          err instanceof Error ? err.message : 'An unknown error occurred',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const formatEventDate = (dateStr: string | null | unknown): string => {
    // Add explicit type check
    if (dateStr === null || typeof dateStr !== 'string') {
      return 'Not set';
    }
    // If it's an empty string after type check, treat as Not Set
    if (dateStr === '' || dateStr === '0001-01-01T00:00:00Z') { // Handle zero date from Go
      return 'Not set';
    }

    try {
      // Parse the YYYY-MM-DD string
      const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, 'PPP'); // e.g., Jun 17, 2024
      }
    } catch (e) {
      console.error('Error parsing event_date:', e);
    }
    return 'Invalid Date';
  };

  const formatCreatedAt = (dateStr: string): string => {
    try {
      // Parse the ISO timestamp string
      const parsedDate = parseISO(dateStr);
      if (isValid(parsedDate)) {
        return format(parsedDate, 'Pp'); // e.g., 06/17/2024, 3:30 PM
      }
    } catch (e) {
      console.error('Error parsing created_at:', e);
    }
    return 'Invalid Date';
  };

  const renderTableContent = () => {
      if (loading) {
          return (
              [...Array(3)].map((_, i) => (
                  <TableRow key={`skel-${i}`}>
                      <TableCell><Skeleton className="h-5 w-3/4" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-1/2" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-1/2" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-8 w-16 ml-auto" /></TableCell>
                  </TableRow>
              ))
          );
      }

      if (error) {
          return (
              <TableRow>
                  <TableCell colSpan={4}>
                      <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Error Loading Events</AlertTitle>
                          <AlertDescription>{error}</AlertDescription>
                      </Alert>
                  </TableCell>
              </TableRow>
          );
      }

      if (events.length === 0) {
          return (
              <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No events found. Create your first event!
                  </TableCell>
              </TableRow>
          );
      }

      return events.map((event) => (
          <TableRow key={event.id}>
              <TableCell className="font-medium">{event.name}</TableCell>
              <TableCell>{formatEventDate(event.event_date)}</TableCell>
              <TableCell>{formatCreatedAt(event.created_at)}</TableCell>
              <TableCell className="text-right">
                  <Button asChild variant="outline" size="sm">
                      <Link to="/events/$eventId/share" params={{ eventId: String(event.id) }}>
                          View Details
                      </Link>
                  </Button>
              </TableCell>
          </TableRow>
      ));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>My Events</CardTitle>
        <Button asChild>
          <Link to="/events/new">
             <PlusCircle className="mr-2 h-4 w-4" /> Create New Event
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Event Date</TableHead>
                <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
             {renderTableContent()}
            </TableBody>
          </Table>
      </CardContent>
    </Card>
  );
}
